import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTimeHasCome } from '@libs/hooks/useTimeHasCome';
import { getLocalStorage } from '@libs/storage';

import { useAuth } from '@features/auth/hooks/useAuth';
import { parseMemberAux } from '@features/club/typings';
import { useUserSocket } from '@features/shared/api';
import {
  useActiveTournamentParticipantsQuery,
  useTournamentCardQuery,
  useTournamentPrivateVideoTicketMutation,
  useTournamentVideoTicketMutation,
} from '@features/tournament/api/hooks';
import { parseParticipantAux } from '@features/tournament/types';
import { useCachedTicket } from '@features/video/hooks/useCachedTicket';
import { usePrivateRoomFlag } from '@features/video/hooks/usePrivateRoomFlag';
import { useVideoStream, VideoStream } from '@features/video/hooks/useVideoStream';
import { decodeTicket, getStreamLink } from '@features/video/lib/ticket';
import { RoomStatus, VideoSource, VideoStreamMode } from '@features/video/types';
import { CamerasSetupModal, StreamCameraProps } from '@features/video/ui/CamerasSetupModal';
import { MicIssueBanner } from '@features/video/ui/MicIssueBanner';

import { VideoStreamContext, VideoStreamContextValue } from './VideoStreamContext';

// Откуда участник снимает каждой камерой в этом турнире: переживает перезагрузку
const sourcesKey = (tournamentId: string) => `video:sources:${tournamentId}`;

type Sources = Record<VideoStreamMode, VideoSource>;

type Props = {
  clubId: string;
  tournamentId: string;
  children: ReactNode;
};

// Трансляция участника живет над страницами турнира и партии: переходы между
// ними ее не прерывают. Модалка настройки тоже здесь, страницы ее только открывают.
// Обе камеры участник выбирает сам: с этого устройства или с телефона.
// Флаг приватной комнаты переводит камеры этого устройства в приватную комнату и обратно
export function VideoStreamProvider({ clubId, tournamentId, children }: Props) {
  const { user } = useAuth();
  const { data: tournament } = useTournamentCardQuery(clubId, tournamentId, { enabled: !!user });
  const isOpen = useTimeHasCome(tournament?.videoOpenTimestampMs);

  // Билеты: групповой и приватный, каждый кешируем до истечения
  const { mutateAsync: requestTicket } = useTournamentVideoTicketMutation(clubId, tournamentId);
  const { ticket, ensureTicket } = useCachedTicket(requestTicket);
  const { mutateAsync: requestPrivateTicket } = useTournamentPrivateVideoTicketMutation(
    clubId,
    tournamentId,
  );
  const { ensureTicket: ensurePrivateTicket } = useCachedTicket(requestPrivateTicket);

  // После окончания турнира видеосвязь живет, пока жив полученный билет
  const claims = ticket ? decodeTicket(ticket) : null;
  const isTicketOver = useTimeHasCome(claims ? claims.exp * 1000 : null);
  const hasLiveTicket = !!claims && !isTicketOver;
  const isClosed =
    tournament?.status === 'Deleted' || (tournament?.status === 'Finished' && !hasLiveTicket);

  // Сеансер — первый участник сеанса, ему видеоконтроль не нужен
  const isSeancer =
    tournament?.system === 'Seance' && tournament.seanceSettings?.seancer?.userId === user?.userId;
  const visible =
    !!tournament?.videoSettings?.enable &&
    tournament.participationStatus === 'Participating' &&
    !isSeancer &&
    !!isOpen &&
    !isClosed;

  // Билет берем сами, не дожидаясь кнопки, как только трансляция доступна (ее старт задан в
  // настройках турнира): по выданным билетам видно, кто пришел на турнир, а кто получил
  // комнату и не подключился. Одна попытка: ошибку покажет вход, он запросит билет снова
  useEffect(() => {
    if (!visible) return;
    ensureTicket().catch(() => {});
  }, [visible, ensureTicket]);

  const [sources, setSources] = useState<Sources>(() => readSources(tournamentId));
  const selectSource = useCallback(
    (mode: VideoStreamMode, next: VideoSource) => {
      setSources((prev) => {
        const sources = { ...prev, [mode]: next };
        writeSources(tournamentId, sources);
        return sources;
      });
    },
    [tournamentId],
  );

  // Флаг приватной комнаты: начало — своя запись в списке участников, дальше события.
  // Список нужен только при видимом видеоконтроле: без него его не грузим
  const { data: participants } = useActiveTournamentParticipantsQuery(
    clubId,
    tournamentId,
    'NumberAsc',
    false,
    { enabled: visible },
  );
  const me = participants?.find((p) => p.userId === user?.userId);
  const [inPrivateRoom, applyAux] = usePrivateRoomFlag(
    me ? parseParticipantAux(me.aux).privateVideoRoom === true : undefined,
  );
  // Сколько камер требует клуб: из aux участника клуба в своей записи, дальше события;
  // не указано — одна, и вторая камера не предлагается
  const [memberAux, setMemberAux] = useState<{ aux: unknown } | null>(null);
  const camerasRequired =
    parseMemberAux(memberAux ? memberAux.aux : me?.memberAux).camerasRequired ?? 1;
  const hasExtra = camerasRequired > 1;
  // менеджеру те же события приходят клубными: он сидит на менеджерском хабе
  useUserSocket((e) => {
    if (!('userId' in e) || e.userId !== user?.userId) return;
    if (
      (e.eventType === 'ParticipantAuxUpdated' || e.eventType === 'ClubParticipantAuxUpdated') &&
      e.tournamentId === tournamentId
    ) {
      applyAux(e.aux);
    } else if (
      (e.eventType === 'MemberAuxUpdated' || e.eventType === 'ClubMemberAuxUpdated') &&
      e.clubId === clubId
    ) {
      setMemberAux({ aux: e.aux });
    }
  });

  // Основная камера говорит и слышит судей, дополнительная по умолчанию молчит
  const main = useVideoStream({ mode: 'main', clubId, tournamentId });
  const extra = useVideoStream({
    mode: 'extra',
    clubId,
    tournamentId,
    micOn: false,
    speakerOn: false,
  });
  const cams = useMemo<Record<VideoStreamMode, VideoStream>>(
    () => ({ main, extra }),
    [main, extra],
  );

  // Билет той комнаты, где камера должна быть сейчас, и флаг, на который каждая камера
  // уже ответила входом или переходом
  const roomTicket = useCallback(
    () => (inPrivateRoom ? ensurePrivateTicket() : ensureTicket()),
    [inPrivateRoom, ensurePrivateTicket, ensureTicket],
  );
  const refRoomFlags = useRef<Record<VideoStreamMode, boolean>>({ main: false, extra: false });
  const joinRoom = useCallback(
    (mode: VideoStreamMode) => {
      refRoomFlags.current[mode] = inPrivateRoom;
      cams[mode].join(roomTicket);
    },
    [inPrivateRoom, cams, roomTicket],
  );

  useAutoJoin('main', visible, sources, main, joinRoom, selectSource);
  useAutoJoin('extra', visible && hasExtra, sources, extra, joinRoom, selectSource);

  // Флаг сменился: камеры в эфире переходят в другую комнату. Не перешли — остаются,
  // где были, без повторов: у судей игрок висит с пометкой «переходит в приват»
  useEffect(() => {
    MODES.forEach((mode) => {
      const cam = cams[mode];
      if (cam.phase !== 'live' || refRoomFlags.current[mode] === inPrivateRoom) return;
      refRoomFlags.current[mode] = inPrivateRoom;
      cam.switchRoom(roomTicket);
    });
  }, [inPrivateRoom, cams, roomTicket]);

  // Видеоконтроль закрылся (бан, конец турнира) — вешаем трубку; вторая камера больше
  // не требуется — тоже
  useEffect(() => {
    if (!tournament) return;
    MODES.forEach((mode) => {
      const closed = !visible || (mode === 'extra' && !hasExtra);
      if (closed && isRoomStatus(cams[mode].phase)) cams[mode].hangUp();
    });
  }, [tournament, visible, hasExtra, cams]);

  // Камеры, которые видит участник: вторая — только по требованию клуба
  const modes = useMemo(() => (hasExtra ? MODES : MODES.slice(0, 1)), [hasExtra]);

  const [isModalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => {
    setModalOpen(true);
    // групповой билет нужен ссылке для телефона
    ensureTicket().catch(() => {});
    modes.forEach((mode) => {
      if (sources[mode] === 'device' && cams[mode].phase === 'idle') cams[mode].openSetup();
    });
  }, [ensureTicket, modes, sources, cams]);
  const closeModal = useCallback(() => {
    setModalOpen(false);
    // не вошли в комнату — отпускаем камеру
    modes.forEach((mode) => {
      if (sources[mode] === 'device' && !isRoomStatus(cams[mode].phase)) {
        cams[mode].closeSetup();
        selectSource(mode, 'none');
      }
    });
  }, [modes, sources, cams, selectSource]);
  const toggleModal = useCallback(
    () => (isModalOpen ? closeModal() : openModal()),
    [isModalOpen, closeModal, openModal],
  );

  const cameras = modes.map((mode) =>
    cameraProps({
      source: sources[mode],
      cam: cams[mode],
      link: ticket ? getStreamLink(clubId, tournamentId, mode, ticket) : undefined,
      onSource: (next) => selectSource(mode, next),
      onJoin: () => joinRoom(mode),
    }),
  );

  // Плашке и кнопке нужно одно состояние: берем камеру, которая снимает с этого устройства
  const device = modes
    .map((mode) => (sources[mode] === 'device' ? cams[mode] : null))
    .filter((cam) => cam !== null);
  const status = device.map((cam) => cam.phase).find(isRoomStatus);
  const error = device.some((cam) => cam.phase === 'error');
  // основная камера с этого устройства: выключенные микрофон или камера (игроком или судьей)
  // подсвечиваем на кнопках
  const isMainLive = sources.main === 'device' && main.phase === 'live';
  const micOff = isMainLive && !main.micOn;
  const camOff = isMainLive && !main.camOn;
  // микрофон или звук молчат в эфире (звонок, перезагрузка без клика): плашка над страницей
  const brokenCam = device.find((cam) => cam.micIssue !== null || cam.audioBlocked);

  const value = useMemo<VideoStreamContextValue>(
    () => ({
      visible,
      source: sources.main,
      status,
      error,
      micOff,
      camOff,
      isModalOpen,
      openModal,
      closeModal,
      toggleModal,
    }),
    [
      visible,
      sources.main,
      status,
      error,
      micOff,
      camOff,
      isModalOpen,
      openModal,
      closeModal,
      toggleModal,
    ],
  );

  return (
    <VideoStreamContext.Provider value={value}>
      {children}
      {isModalOpen && <CamerasSetupModal cameras={cameras} onClose={closeModal} />}
      {brokenCam && (
        <MicIssueBanner
          issue={brokenCam.micIssue}
          audioBlocked={brokenCam.audioBlocked}
          onFix={brokenCam.fixMic}
        />
      )}
    </VideoStreamContext.Provider>
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

const MODES: VideoStreamMode[] = ['main', 'extra'];

// После перезагрузки продолжаем трансляцию с этого устройства без лишних кликов;
// комнату закрыл сервер — трансляция больше не нужна
function useAutoJoin(
  mode: VideoStreamMode,
  visible: boolean,
  sources: Sources,
  cam: VideoStream,
  joinRoom: (mode: VideoStreamMode) => void,
  selectSource: (mode: VideoStreamMode, next: VideoSource) => void,
) {
  const source = sources[mode];
  const { phase } = cam;
  const refJoined = useRef(false);

  useEffect(() => {
    if (refJoined.current || !visible || source !== 'device' || phase !== 'idle') return;
    refJoined.current = true;
    joinRoom(mode);
  }, [visible, source, phase, joinRoom, mode]);

  useEffect(() => {
    if (phase === 'ended') selectSource(mode, 'none');
  }, [phase, mode, selectSource]);
}

// Блок камеры в модалке: выбор способа → настройка → эфир, или ссылка для телефона
function cameraProps({
  source,
  cam,
  link,
  onSource,
  onJoin,
}: {
  source: VideoSource;
  cam: VideoStream;
  link?: string;
  onSource: (next: VideoSource) => void;
  onJoin: () => void;
}): StreamCameraProps {
  if (source === 'other') {
    return { mode: 'other', link, onBack: () => onSource('none') };
  }
  if (source === 'device' && isRoomStatus(cam.phase)) {
    return {
      mode: 'device',
      status: cam.phase,
      setup: { devices: cam.devices, meterTrack: cam.meterTrack, mirrored: true },
      micOn: cam.micOn,
      camOn: cam.camOn,
      onToggleMic: cam.toggleMic,
      onToggleCam: cam.toggleCam,
      onHangUp: () => {
        cam.hangUp();
        onSource('none');
      },
    };
  }
  if (source === 'device') {
    return {
      mode: 'setup',
      setup: {
        devices: cam.devices,
        mirrored: true,
        error: cam.error,
        onJoin,
        onCancel: () => {
          cam.closeSetup();
          onSource('none');
        },
      },
    };
  }
  return {
    mode: 'off',
    onUseDevice: () => {
      onSource('device');
      cam.openSetup();
    },
    onUseOther: () => onSource('other'),
  };
}

function isRoomStatus(phase: string): phase is RoomStatus {
  return (
    phase === 'connecting' || phase === 'waiting' || phase === 'live' || phase === 'reconnecting'
  );
}

function readSources(tournamentId: string): Sources {
  const none: Sources = { main: 'none', extra: 'none' };
  try {
    const raw = getLocalStorage().getItem(sourcesKey(tournamentId));
    if (!raw) return none;
    const parsed = JSON.parse(raw);
    const pick = (v: unknown): VideoSource => (v === 'device' || v === 'other' ? v : 'none');
    return { main: pick(parsed.main), extra: pick(parsed.extra) };
  } catch {
    return none;
  }
}

function writeSources(tournamentId: string, sources: Sources): void {
  const storage = getLocalStorage();
  if (sources.main === 'none' && sources.extra === 'none') {
    storage.removeItem(sourcesKey(tournamentId));
  } else {
    storage.setItem(sourcesKey(tournamentId), JSON.stringify(sources));
  }
}
